<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
  import { useAppConfig } from 'nuxt/app'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import useOutbrainAds, {
    type OutbrainElementData,
  } from '../../composables/ad-ops/useOutbrainAds'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import { createExtrnId } from '../../utils/string/uuid'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'
  import debug from '../../utils/console'

  const logger = debug.withTag('Outbrain:DXT-1780')

  interface OutbrainWidgetAttributes {
    'data-ob-portalUrl': string
    'data-ob-language': string
    'data-widget-id': string
    'data-external-id': string
    'data-ob-installation-key': string
    'data-external-secondary-id': string
    'data-render-registered': boolean
  }

  /**
   * Props for the Outbrain component
   * @property placement - The placement identifier for the ad
   * @property widgetId - The Outbrain widget ID
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property  lazyRender - controls -visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * AND visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adInstanceId - Optional unique identifier for the ad instance
   * @property sticky - Optional sticky class for the ad
   */
  export interface OutbrainProps {
    placement: string
    widgetId: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<OutbrainProps>(), {
    tilePosition: -1,
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
  })

  if (!props.placement || !props.widgetId) {
    throw new Error(
      `Outbrain component requires placement and widgetId props- ${props.placement}`
    )
  }

  const { outbrain } = useAppConfig()
  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()

  const { track } = useAnalytics()

  const { callRenderResearchWidgets, triggerOutbrainRenderedEvent } =
    useOutbrainAds()

  const placementClassName = ref<string>(props.placement)
  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')
  const adBrand = ref<string>('')
  const hasTrackedRender = ref<boolean>(false) // Track if we've already fired the event

  const widgetAttributes = computed<OutbrainWidgetAttributes>(() => {
    const widgetAttributes = {
      'data-ob-portalUrl': '',
      'data-ob-language': 'en',
      'data-widget-id': props.widgetId,
      'data-external-id': '',
      'data-ob-installation-key': outbrain?.installationKey || '',
      'data-external-secondary-id': createExtrnId(),
      'data-render-registered': false,
    }

    if (import.meta.client) {
      widgetAttributes[`data-ob-portalUrl`] = getTruncatedUrl()
      widgetAttributes[`data-external-id`] = window.location.host
    }

    return widgetAttributes
  })
  function setAdBrand(element: Element) {
    adBrand.value = element?.querySelector('.ob-rec-source')?.textContent || ''
  }

  function getEventData(): NtpAdEvent {
    const eventData: NtpAdEvent = {
      ntp_tile_position: props.tilePosition,
      ntp_ad_provider: 'outbrain',
      ntp_creative_id: props.widgetId,
      ntp_placement_name: props.placement,
      ntp_ad_brand: adBrand.value,
    }

    return eventData
  }

  function handleAdClick() {
    adClicked(getEventData(), props.widgetId)
  }

  function attachClickEventToAnchorTags(element: Element) {
    const anchorTag = element.querySelector('a')

    if (anchorTag && !anchorTag.getAttribute('data-click-registered')) {
      anchorTag.setAttribute('data-click-registered', 'true')
      anchorTag.addEventListener('click', handleAdClick)
    }
  }

  function detachClickEventFromAnchorTags(element: Element) {
    // Find all anchor tags with our registered click handlers and remove the listeners
    const registeredAnchors = element.querySelectorAll(
      'a[data-click-registered="true"]'
    )
    registeredAnchors.forEach((anchor) => {
      anchor.removeEventListener('click', handleAdClick)
      anchor.removeAttribute('data-click-registered')
    })
  }

  async function handleOutbrainRenderedEvent(data: OutbrainElementData) {
    // Outbrain Render Event Handler
    //
    // The 'rendered' event fires multiple times during the ad rendering process:
    // 1. Initial fires: Returns wrapper element only (no actual ad content)
    // 2. DOM attachment: Element may not be in DOM tree yet (no parentElement)
    // 3. Final render: Ad is fully rendered and attached to DOM
    //
    // Implementation Strategy:
    // - Use parentElement presence as render completion indicator
    // - Verify correct placement via expected class name
    // - Track render status to prevent duplicate processing
    // - Process only first successful render
    // - Mark as rendered after processing

    //await for rendered element to be in DOM (renderOutbrainElement)
    await nextTick()

    const element: Element | null = data.element

    const parentElement: HTMLElement | null =
      element?.parentElement?.parentElement?.parentElement || null

    // Check the component-level flag instead of DOM attributes
    if (hasTrackedRender.value) {
      logger.debug(
        '[handleOutbrainRenderedEvent] Already tracked render - skipping',
        {
          placement: props.placement,
          widgetId: props.widgetId,
        }
      )
      return
    }

    const hasExpectedClass = parentElement?.classList.contains(props.placement)

    // DXT-1780: Comprehensive diagnostic logging
    const diagnosticData = {
      placement: props.placement,
      placementClassName: placementClassName.value,
      widgetId: props.widgetId,
      hasElement: !!element,
      hasParentElement: !!parentElement,
      parentElementTag: parentElement?.tagName,
      parentElementClasses: parentElement
        ? Array.from(parentElement.classList)
        : [],
      expectedClass: props.placement,
      hasExpectedClass,
      hasTrackedRender: hasTrackedRender.value,
      widgetWrapperRefExists: !!widgetWrapperEl.value,
      widgetWrapperRefClasses: widgetWrapperEl.value
        ? Array.from(widgetWrapperEl.value.classList)
        : [],
      timestamp: Date.now(),
    }

    logger.debug(
      '[handleOutbrainRenderedEvent] Render event fired',
      diagnosticData
    )

    // DXT-1780: Log different rejection paths
    if (!parentElement) {
      let elementParentCount = 0
      if (element) {
        let currentElement: Element | null = element
        while (currentElement.parentElement && elementParentCount < 3) {
          currentElement = currentElement.parentElement
          elementParentCount++
        }
      }

      logger.warn(
        '[handleOutbrainRenderedEvent] No parent element - DOM not ready?',
        {
          placement: props.placement,
          hasElement: !!element,
          elementParentCount,
        }
      )
      return
    }

    if (!hasExpectedClass) {
      logger.warn(
        '[handleOutbrainRenderedEvent] Class mismatch - event may be lost',
        {
          placement: props.placement,
          expectedClass: props.placement,
          placementClassName: placementClassName.value,
          parentElementClasses: Array.from(parentElement.classList),
          widgetWrapperClasses: widgetWrapperEl.value
            ? Array.from(widgetWrapperEl.value.classList)
            : [],
        }
      )
      return
    }

    // Success path - mark as rendered IMMEDIATELY to prevent race condition
    if (parentElement && !hasTrackedRender.value && hasExpectedClass) {
      // Set render flag synchronously before any async operations
      // This prevents duplicate events if handleOutbrainRenderedEvent is called multiple times
      hasTrackedRender.value = true

      logger.debug(
        '[handleOutbrainRenderedEvent] Processing render - tracking event',
        {
          placement: props.placement,
          widgetId: props.widgetId,
        }
      )

      // Set ad brand for analytics
      setAdBrand(element)

      attachClickEventToAnchorTags(data.element)

      const eventData = getEventData()

      logger.debug(
        '[handleOutbrainRenderedEvent] Tracking NtpAd_Rendered',
        eventData
      )

      track('NtpAd_Rendered', eventData)

      logger.debug('[handleOutbrainRenderedEvent] Event tracked successfully', {
        placement: props.placement,
      })
    }
  }

  const renderOutbrainElement = ref(false)

  async function renderOutbrainAd() {
    renderOutbrainElement.value = true

    await nextTick()

    if (
      !widgetWrapperEl.value ||
      widgetWrapperEl.value?.getAttribute('data-render-registered')
    ) {
      // DXT-1780: Log early return cases
      logger.debug('[renderOutbrainAd] Early return', {
        placement: props.placement,
        hasWrapper: !!widgetWrapperEl.value,
        isAlreadyRegistered: widgetWrapperEl.value?.getAttribute(
          'data-render-registered'
        ),
      })
      return
    }

    const index = getIndexOfPlacement(props.placement, widgetWrapperEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    // DXT-1780: Log setup details
    logger.debug('[renderOutbrainAd] Setting up Outbrain ad', {
      placement: props.placement,
      widgetId: props.widgetId,
      index,
      placementClassName: placementClassName.value,
      lazyRender: props.lazyRender,
      sticky: props.sticky,
      adInstanceId: props.adInstanceId,
    })
    try {
      logger.debug('[renderOutbrainAd] Triggering Outbrain rendered event', {
        widgetId: props.widgetId,
        placement: props.placement,
      })

      callRenderResearchWidgets()
      await triggerOutbrainRenderedEvent(
        props.widgetId,
        handleOutbrainRenderedEvent
      )
    } catch (error) {
      track('NtpAd_Render_Failed', getEventData())
      logger.error('[renderOutbrainAd] Error rendering Outbrain ad', {
        placement: props.placement,
        widgetId: props.widgetId,
        error,
      })
    }
  }

  onMounted(() => {
    // DXT-1780: Log mount and lazy render config
    logger.debug('[onMounted] Outbrain component mounted', {
      placement: props.placement,
      widgetId: props.widgetId,
      lazyRender: props.lazyRender,
      sticky: props.sticky,
      tilePosition: props.tilePosition,
      adInstanceId: props.adInstanceId,
    })

    renderWhenCurrentElementIsVisible(
      widgetWrapperEl.value,
      props.lazyRender,
      props.sticky,
      renderOutbrainAd
    )
  })

  onUnmounted(() => {
    if (widgetWrapperEl.value) {
      detachClickEventFromAnchorTags(widgetWrapperEl.value)
    }
  })

  // Expose methods for testing
  defineExpose({
    renderOutbrainAd,
    handleOutbrainRenderedEvent,
  })
</script>

<template>
  <div ref="widgetWrapperEl" :class="placementClassName">
    <div
      v-if="renderOutbrainElement"
      class="OUTBRAIN"
      :class="placementClassName"
      v-bind="widgetAttributes"
    />
  </div>
</template>
